import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { logOut, setErrorMessage } from "../redux/actions";
import { transformResponse } from "./transform";

const baseQuery = fetchBaseQuery({
  baseUrl: `${import.meta.env.VITE_BACKEND_BASE_URL}/v1`,
  credentials: "include",
  prepareHeaders: (headers) => {
    headers.set("accept", "application/json");
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    api.dispatch(logOut());
  } else if (result.error) {
    const errorMessage =
      result.error.data || result.error.message || "An error occurred";
    api.dispatch(setErrorMessage(`Error: ${errorMessage}`));
  }

  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Project", "UserFavorites", "Report", "BenchmarkReport", "BenchmarkTable"],
  endpoints: (builder) => ({
    // ===== PROJECT ENDPOINTS =====
    getProjects: builder.query({
      query: ({ type, page = 1, search } = {}) => {
        const params = new URLSearchParams();
        if (type) params.append("type", type);
        if (page > 1) params.append("page", page.toString());
        if (search) params.append("search", search);
        return `projects?${params.toString()}`;
      },
      providesTags: (result, error, { type }) => {
        if (result) {
          return [
            ...result.map(({ _id }) => ({ type: "Project", id: _id })),
            { type: "Project", id: "LIST" },
            ...(type === "favorites"
              ? [{ type: "UserFavorites", id: "LIST" }]
              : []),
          ];
        }
        return [{ type: "Project", id: "LIST" }];
      },
    }),

    getProject: builder.query({
      query: (id) => `projects/${id}`,
      providesTags: (result, error, id) => [{ type: "Project", id }],
    }),

    createProject: builder.mutation({
      query: (newProject) => ({
        url: "projects",
        method: "POST",
        body: newProject,
      }),
      invalidatesTags: [{ type: "Project", id: "LIST" }],
    }),

    updateProject: builder.mutation({
      query: ({ patch }) => ({
        url: `projects/${patch._id}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (result, error, { patch }) => {
        return [
          { type: "Project", id: patch._id },
          { type: "Project", id: "LIST" },
          // This will now invalidate reports in the same cache!
        ];
      },
    }),

    deleteProject: builder.mutation({
      query: (id) => ({
        url: `projects/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Project", id },
        { type: "Project", id: "LIST" },
      ],
    }),

    toggleFavorite: builder.mutation({
      query: (id) => ({
        url: `projects/fav/${id}`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "UserFavorites", id: "LIST" },
        { type: "Project", id: "LIST" },
      ],
    }),

    getMe: builder.query({
      query: () => "me",
    }),

    // ===== REPORT ENDPOINTS =====
    getReportById: builder.query({
      query: ({ id, dateFrom, dateTo }) => ({
        url: `reports/${id}`,
        ...(dateFrom &&
          dateTo && {
            params: {
              dateFrom: dateFrom,
              dateTo: dateTo,
            },
          }),
      }),
      transformResponse: (data) => transformResponse(data),
      transformErrorResponse: (response, meta, arg) => {
        let errorMessage = "Error loading report";
        if (response.status && response.data) {
          errorMessage += `: ${response.data}`;
        }
        return {
          originalError: response,
          message: errorMessage,
          status: response.status,
          isAuthError: response.status === 401,
        };
      },
      providesTags: (result, error, { id }) => {
        return [
          { type: "Report", id },
          { type: "Report", id: "LIST" },
          // Tag with the project ID - now in the same cache as projects!
          ...(result?.project?._id
            ? [{ type: "Project", id: result.project._id }]
            : []),
        ];
      },
    }),

    // ===== BENCHMARK ENDPOINTS =====
    getMediaTitles: builder.query({
      query: () => "benchmark/media-titles",
    }),

    createBenchmarkReport: builder.mutation({
      query: ({ name, dateFrom, dateTo }) => ({
        url: "benchmark/reports",
        method: "POST",
        body: { name, dateFrom, dateTo },
      }),
      invalidatesTags: [{ type: "BenchmarkReport", id: "LIST" }],
    }),

    getBenchmarkReports: builder.query({
      query: () => "benchmark/reports",
      providesTags: [{ type: "BenchmarkReport", id: "LIST" }],
    }),

    updateBenchmarkReport: builder.mutation({
      query: ({ reportId, name, dateFrom, dateTo }) => ({
        url: `benchmark/reports/${reportId}`,
        method: "PUT",
        body: { name, dateFrom, dateTo },
      }),
      invalidatesTags: [{ type: "BenchmarkReport", id: "LIST" }],
    }),

    createBenchmarkTable: builder.mutation({
      query: ({ reportId, mediaTitles, metrics, type }) => ({
        url: `benchmark/reports/${reportId}/tables`,
        method: "POST",
        body: { mediaTitles, metrics, type },
      }),
      invalidatesTags: (result, error, { reportId }) => [
        { type: "BenchmarkTable", id: `LIST-${reportId}` },
      ],
    }),

    getBenchmarkTables: builder.query({
      query: (reportId) => `benchmark/reports/${reportId}/tables`,
      providesTags: (result, error, reportId) => [
        { type: "BenchmarkTable", id: `LIST-${reportId}` },
      ],
    }),

    deleteBenchmarkTable: builder.mutation({
      query: ({ reportId, tableId }) => ({
        url: `benchmark/reports/${reportId}/tables/${tableId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { reportId }) => [
        { type: "BenchmarkTable", id: `LIST-${reportId}` },
      ],
    }),

    deleteBenchmarkReport: builder.mutation({
      query: (reportId) => ({
        url: `benchmark/reports/${reportId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "BenchmarkReport", id: "LIST" }],
    }),
  }),
});

export const {
  // Project hooks
  useGetProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useToggleFavoriteMutation,
  useGetMeQuery,
  // Report hooks
  useGetReportByIdQuery,
  // Benchmark hooks
  useGetMediaTitlesQuery,
  useCreateBenchmarkReportMutation,
  useUpdateBenchmarkReportMutation,
  useDeleteBenchmarkReportMutation,
  useGetBenchmarkReportsQuery,
  useCreateBenchmarkTableMutation,
  useGetBenchmarkTablesQuery,
  useDeleteBenchmarkTableMutation,
} = api;
